import { autoinject, bindable, Container } from 'aurelia-framework';
import { SearchResult } from 'elements/search/search-input';
import { PubSub } from 'lib/event/PubSub';
import { EqualityChecker, Keyboard } from 'lib/helpers';
import { AsyncErrorHandler } from 'lib/ui';
import {
  MooringProductionFleetAnchorLineSlotDto,
  MooringArticleSearchResult,
  MooringComponentType,
  UpdateMooringProductionFleetAnchorLineDto,
} from 'models/mooring';
import { ToastService } from 'services';
import { MooringComponentSearchHandler, MooringService } from 'services/mooring';
import { OfferViewContext } from '../../../views/mooring/mooring-offers/OfferViewContext';
import { MooringComponentSearchResult } from 'services/mooring/mooring-component-search-handler';

type ComponentSlotName =
  | 'Anchor'
  | 'AnchoringShackle'
  | 'BottomChain'
  | 'SpliceBottomChainShackle'
  | 'RopeShackle'
  | 'Thimble'
  | 'Rope'
  | 'AnchorLineStrap';

@autoinject()
export class MooringProductionFleetAnchorLinesTable {
  @bindable mooringId!: number;
  @bindable show = true;

  protected context = OfferViewContext.useContext(this.container);
  protected anchorLines: UpdateMooringProductionFleetAnchorLineDto[] = [];
  protected editRows = false;
  protected changeTracker = new EqualityChecker();

  constructor(
    private container: Container,
    private mooringService: MooringService,
    private pubsub: PubSub,
    private toast: ToastService,
    protected mooringComponentSearchHandler: MooringComponentSearchHandler
  ) {}

  protected bind() {
    if (!this.mooringId) {
      throw new Error(`Mooring id is missing.`);
    }
  }

  protected async attached() {
    await this.mooringComponentSearchHandler.preCache();
    await this.getAnchorLines();

    this.pubsub.sub('list-entity:updated', () => {
      void this.getAnchorLines();
    });
    this.pubsub.sub('entity:updated', () => {
      void this.getAnchorLines();
    });
  }

  protected async setEdit() {
    if (this.editRows) {
      await this.getAnchorLines();
      this.editRows = false;
      return;
    }

    this.editRows = true;
  }

  protected hasChanges() {
    return this.changeTracker.hasChanges(this.anchorLines);
  }

  protected onSlotQuantityChanged(line: UpdateMooringProductionFleetAnchorLineDto, slotName: ComponentSlotName, value: number) {
    const slot = this.getSlot(line, slotName);
    if (!slot) {
      return;
    }

    slot.Quantity = value;
  }

  protected onArticleSelected(searchResult: MooringComponentSearchResult, slot: MooringProductionFleetAnchorLineSlotDto) {
    if (!searchResult?.id || !slot) return;
    const article = searchResult.return;
    if (!article) return;
    slot.ArticleProductId = article.Id;
    slot.ProductName = article.ProductName;
  }

  protected canEditSlot(slot: MooringProductionFleetAnchorLineSlotDto) {
    return !!slot && (!!slot.ComponentId || !!slot.ArticleProductId || slot.ComponentCount > 0);
  }

  protected canEditSingleQuantity(slot: MooringProductionFleetAnchorLineSlotDto) {
    return this.canEditSlot(slot) && slot.ComponentCount === 1;
  }

  protected getDisplayedRopeType(line: UpdateMooringProductionFleetAnchorLineDto) {
    if (this.editRows && line.RopeSlot && line.RopeSlot.ProductName) {
      return line.RopeSlot.ProductName;
    }

    return line.RopeType;
  }

  @AsyncErrorHandler
  protected async save() {
    await this.mooringService.updateProductionFleetAnchorLines(this.mooringId, this.anchorLines);

    this.toast.showUpdated();
    this.pubsub.publish('list-entity:updated', {
      name: MooringComponentType.anchorLine,
    });
    this.editRows = false;
    await this.getAnchorLines();
  }

  @AsyncErrorHandler
  private async getAnchorLines() {
    const { data } = await this.mooringService.getProductionFleetAnchorLines(this.mooringId);
    this.anchorLines = structuredClone(data) as UpdateMooringProductionFleetAnchorLineDto[];

    this.changeTracker = new EqualityChecker();
    this.changeTracker.track(this.anchorLines);
  }

  private getSlot(line: UpdateMooringProductionFleetAnchorLineDto, slotName: ComponentSlotName) {
    const slots: Record<ComponentSlotName, MooringProductionFleetAnchorLineSlotDto> = {
      Anchor: line.AnchorSlot,
      AnchoringShackle: line.AnchoringShackleSlot,
      BottomChain: line.BottomChainSlot,
      SpliceBottomChainShackle: line.SpliceBottomChainShackleSlot,
      RopeShackle: line.RopeShackleSlot,
      Thimble: line.ThimbleSlot,
      Rope: line.RopeSlot,
      AnchorLineStrap: line.AnchorLineStrapSlot,
    };

    return slots[slotName];
  }

  protected unbind() {
    this.pubsub.unsub();
  }

  protected detached() {
    this.pubsub.unsub();
  }

  /**
   * Handles the Enter key to navigate to the next line in the table.
   * If Shift + Enter is pressed, it navigates to the previous line.
   * field is the name of the field.
   * @param event The keyboard event.
   * @param parentIndex The index of the outer table row, not the article subtable index.
   * @param field The field name to identify the property/input.
   */
  protected tryGotoNextLine(event: KeyboardEvent, parentIndex: number, field: string) {
    console.log("parentIndex", parentIndex);

    
    const isEnterKey = Keyboard.isEnterKey(event);
    const isShiftEnter = Keyboard.isShiftEnterKey(event);

    if (!isEnterKey && !isShiftEnter) {
      return;
    }

    event.preventDefault();

    let nextIndex = isShiftEnter ? parentIndex - 1 : parentIndex + 1;
    nextIndex = Math.max(0, Math.min(this.anchorLines.length - 1, nextIndex));

    const nextRow = document.querySelector(`[data-identifier="${field}-${nextIndex}"]`);

    if (!nextRow) {
      return;
    }

    nextRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
    (nextRow as HTMLInputElement | HTMLTextAreaElement | null)?.focus();
  }
}
